import React from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { MantineProvider } from "@mantine/core";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import { Userpilot } from "userpilot";
import { AutoLogout } from "shared/src/components/AutoLogout";
import ProductLayout from "./components/Product/Layout/Layout";
import productRoutes from "./routes/productRoutes";
import productEligibilityRoutes from "./routes/productEligibilityRoutes";
import accountRoutes from "./routes/accountRoutes";
import routesWithoutSignup from "./routes/routesWithoutSignup";
import QueryAuthGate from "./routes/queryAuthGate/QueryAuthGate";
import { ProductContextProvider } from "./contexts/ProductContext";
import { EligibilityProvider } from "./contexts/EligibilityContext";
import { AppealIdProvider } from "./contexts/AppealIdContext";
import { PatientProvider } from "./contexts/PatientContext";
import { RequireAppealId } from "./components/Shared/RequireAppealId";
import ClaimableAuthenticator from "./components/Shared/ClaimableAuthenticator";
import Page from "./components/Product/Layout/Page";
import theme from "./theme";
import IdentifyUser from "./components/IdentifyUser";
import PathConstants from "./routes/pathConstants";
import { MetaProvider } from "./contexts/MetaContext";
import { InitialAppealProvider } from "./contexts/InitialAppealContext";

import "@aws-amplify/ui-react/styles.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID,
      allowGuestAccess: true,
    },
  },
});

Userpilot.initialize("NX-32bc6362");

const ResumeAppeal = React.lazy(
  () => import("./pages/Product/Resume/ResumeAppeal"),
);

const Appeals = React.lazy(() => import("./pages/Product/Account/Appeals"));
const ReportAppealResultPage = React.lazy(
  () => import("./pages/Product/ReportingFlow/ReportAppealResult"),
);

function AutoLogoutWrapper() {
  const { signOut, authStatus } = useAuthenticator();

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    signOut();
  };

  if (authStatus !== "authenticated") {
    return null;
  }

  return <AutoLogout onLogout={handleLogout} />;
}

function App() {
  const router = createBrowserRouter([
    {
      element: (
        <ClaimableAuthenticator>
          <ProductContextProvider>
            <RequireAppealId>
              <ProductLayout />
            </RequireAppealId>
          </ProductContextProvider>
        </ClaimableAuthenticator>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: productRoutes,
    },
    {
      element: (
        <ProductContextProvider>
          <ProductLayout />
        </ProductContextProvider>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: productEligibilityRoutes,
    },
    {
      element: (
        <ClaimableAuthenticator defaultTab="signIn">
          <ProductContextProvider>
            <ProductLayout />
          </ProductContextProvider>
        </ClaimableAuthenticator>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: accountRoutes,
    },
    {
      element: (
        <ClaimableAuthenticator defaultTab="signIn" hideSignUp>
          <ProductContextProvider>
            <ProductLayout />
          </ProductContextProvider>
        </ClaimableAuthenticator>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: routesWithoutSignup,
    },
    {
      element: (
        <ProductContextProvider>
          <ProductLayout />
        </ProductContextProvider>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: [
        { path: PathConstants.ResumeAppeal, element: <ResumeAppeal /> },
        {
          path: PathConstants.Appeals,
          element: (
            <QueryAuthGate>
              <Appeals />
            </QueryAuthGate>
          ),
        },
        {
          path: PathConstants.ReportAppealResult,
          element: (
            <QueryAuthGate>
              <ReportAppealResultPage />
            </QueryAuthGate>
          ),
        },
      ],
    },
  ]);

  return (
    <MantineProvider theme={theme}>
      <MetaProvider>
        <Authenticator.Provider>
          <AppealIdProvider>
            <PatientProvider>
              <InitialAppealProvider>
                <EligibilityProvider>
                  <RouterProvider router={router} />
                </EligibilityProvider>
              </InitialAppealProvider>
            </PatientProvider>
          </AppealIdProvider>
          <IdentifyUser />
          <AutoLogoutWrapper />
        </Authenticator.Provider>
      </MetaProvider>
    </MantineProvider>
  );
}

export default App;
